// src/router/index.ts
import { createRouter, createWebHistory, isNavigationFailure, NavigationFailureType, RouteLocationNormalized } from 'vue-router'
import Home from "@/pages/Home.vue"
import AdminDashboard from "@/pages/AdminDashboard.vue"
import AdminUsers from "@/pages/admin/AdminUsers.vue"
import UserDashboard from "@/pages/UserDashboard.vue"
import Login from "@/pages/Login.vue"
import EmployeeForm from "@/pages/EmployeeForm.vue"
import EmployeeView from "@/pages/EmployeeView.vue"
import EmployeeSearch from "@/pages/EmployeeSearch.vue"
import WikiHome from "@/pages/wiki/WikiHome.vue"
import WikiArticle from "@/pages/wiki/WikiArticle.vue"
import WikiEditor from "@/pages/wiki/WikiEditor.vue"
import TagUsage from "@/pages/TagUsage.vue"
import { useAuthStore } from '@/store/auth'

const routes = [
  { path: "/", component: Home },
  { path: "/login", component: Login },
  { path: "/admin", component: AdminDashboard, meta: { requiresAdmin: true } },
  { path: "/admin/users", component: AdminUsers, meta: { requiresAdmin: true } },
  { path: "/general", component: UserDashboard },
  {
    path: "/employee/add",
    component: EmployeeForm,
    props: { editMode: false },
  },
  { path: "/employee/all", component: EmployeeSearch },
  {
    path: "/employee/:id/edit",
    name: "EmployeeEdit",
    component: EmployeeForm,
    props: (route: RouteLocationNormalized) => ({
      editMode: true,
      employeeId: Number(route.params.id),
    }),
  },
  {
    path: "/employee/:id",
    component: EmployeeView,
    name: "EmployeeView",
    props: (route: RouteLocationNormalized) => ({
      employeeId: Number(route.params.id),
    }),
  },
  { path: "/wiki", component: WikiHome },
  {
    path: "/wiki/new",
    component: WikiEditor,
    meta: { requiresAdmin: true },
  },
  {
    path: "/wiki/:slug/edit",
    component: WikiEditor,
    meta: { requiresAdmin: true },
  },
  { path: "/wiki/:slug", component: WikiArticle },
  {
    path: "/tags/:slug",
    name: "TagUsage",
    component: TagUsage,
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach((to) => {
  const auth = useAuthStore()
  if (to.meta?.requiresAdmin && !auth.isAdminRole) {
    return '/login'
  }
  if (to.path === '/' && auth.isLoggedIn) {
    if (auth.isAdminRole) return '/admin'
    if (auth.permission === 'employee') return '/employee/all'
    return '/general'
  }
  return true
})

// ---- Global override to suppress redundant navigation errors ----
const originalPush = (router as any).push
router.push = function (location: any) {
  return originalPush.call(this, location).catch((err: any) => {
    if (isNavigationFailure(err, NavigationFailureType.duplicated)) {
      // Ignore the redundant navigation error
      return err
    }
    // Re-throw other errors so they don't get accidentally swallowed
    return Promise.reject(err)
  })
}

const originalReplace = (router as any).replace
router.replace = function (location: any) {
  return originalReplace.call(this, location).catch((err: any) => {
    if (isNavigationFailure(err, NavigationFailureType.duplicated)) {
      return err
    }
    return Promise.reject(err)
  })
}
// -------------------------------------------------------------------

export default router
